import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import api from '../services/api';

const Signup = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [promoStatus, setPromoStatus] = useState(null); // null, 'validating', 'valid', 'invalid'
  const [promoCredits, setPromoCredits] = useState(0);
  const [promoError, setPromoError] = useState('');
  const [captchaId, setCaptchaId] = useState('');
  const [captchaImage, setCaptchaImage] = useState('');
  const [captchaText, setCaptchaText] = useState('');
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  // Load captcha on mount
  useEffect(() => {
    loadCaptcha();
  }, []);

  const loadCaptcha = async () => {
    try {
      const response = await api.get('/auth/captcha');
      setCaptchaId(response.data.captchaId);
      setCaptchaImage(response.data.captchaImage);
      setCaptchaText('');
    } catch (error) {
      console.error('Failed to load captcha:', error);
    }
  };

  // Validate promo code when user finishes typing
  const validatePromoCode = async (code) => {
    if (!code || !code.trim()) {
      setPromoStatus(null);
      setPromoCredits(0);
      setPromoError('');
      return;
    }

    setPromoStatus('validating');
    try {
      const response = await api.post('/auth/validate-promo', { promoCode: code });
      setPromoStatus('valid');
      setPromoCredits(response.data.credits);
      setPromoError('');
    } catch (error) {
      setPromoStatus('invalid');
      setPromoCredits(0);
      setPromoError(error.response?.data?.error || 'Invalid promo code');
    }
  };

  // Debounce promo code validation
  useEffect(() => {
    const timer = setTimeout(() => {
      if (promoCode.trim()) {
        validatePromoCode(promoCode);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [promoCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (!captchaText) {
      toast.error('Please enter the captcha');
      return;
    }

    // Warn about invalid promo code but allow signup (code just won't apply)
    if (promoCode.trim() && promoStatus === 'invalid') {
      toast.warning('Promo code is invalid and won\'t be applied');
    }

    // Wait for promo validation to complete
    if (promoCode.trim() && promoStatus === 'validating') {
      toast.error('Please wait for promo code validation');
      return;
    }

    setLoading(true);

    try {
      // Only send promo code if it's valid
      const validPromoCode = promoStatus === 'valid' ? promoCode : '';
      const result = await signup(email, password, name, captchaId, captchaText, validPromoCode);
      if (validPromoCode && result.user?.credits > 0) {
        toast.success(`🎄 Account created! You received ${result.user.credits} free message${result.user.credits === 1 ? '' : 's'}!`);
        navigate('/dashboard');
      } else {
        toast.success('🎄 Account created! Welcome to Jingle Texts!');
        // Redirect to credits page to prompt purchase
        navigate('/credits');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Signup failed');
      // Reload captcha on failure
      loadCaptcha();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1 className="auth-title">🎄 Join the Magic!</h1>
        <p className="auth-subtitle">Create your account to start sending festive messages</p>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="auth-form-grid">
            <div className="form-group">
              <label className="form-label">Your Name</label>
              <input
                type="text"
                className="form-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Santa's Helper"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input
                type="email"
                className="form-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Password</label>
              <input
                type="password"
                className="form-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={6}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Confirm Password</label>
              <input
                type="password"
                className="form-input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">🎁 Promo Code <span style={{ color: 'var(--text-secondary)', fontWeight: 'normal' }}>(optional)</span></label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                className="form-input"
                value={promoCode}
                onChange={(e) => {
                  setPromoCode(e.target.value.toUpperCase());
                  if (!e.target.value.trim()) {
                    setPromoStatus(null);
                    setPromoCredits(0);
                    setPromoError('');
                  }
                }}
                placeholder="Promo Code"
                style={{ 
                  textTransform: 'uppercase',
                  borderColor: promoStatus === 'valid' ? 'var(--christmas-green-light)' : 
                               promoStatus === 'invalid' ? 'var(--christmas-red)' : undefined,
                  paddingRight: promoStatus ? '40px' : undefined
                }}
              />
              {promoStatus === 'validating' && (
                <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }}>⏳</span>
              )}
              {promoStatus === 'valid' && (
                <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--christmas-green-light)' }}>✓</span>
              )}
              {promoStatus === 'invalid' && (
                <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--christmas-red)' }}>✗</span>
              )}
            </div>
            {promoStatus === 'valid' && (
              <small style={{ color: 'var(--christmas-green-light)', fontSize: '0.85rem', marginTop: '5px', display: 'block' }}>
                🎉 Valid! You'll receive {promoCredits} free {promoCredits === 1 ? 'message' : 'messages'}!
              </small>
            )}
            {promoStatus === 'invalid' && (
              <small style={{ color: 'var(--christmas-red)', fontSize: '0.85rem', marginTop: '5px', display: 'block' }}>
                ❌ {promoError}
              </small>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">🔐 Security Check</label>
            <div className="captcha-container">
              {captchaImage && (
                <img 
                  src={captchaImage} 
                  alt="Captcha" 
                  className="captcha-image"
                  onClick={loadCaptcha}
                  title="Click to refresh"
                />
              )}
              <button 
                type="button" 
                className="captcha-refresh"
                onClick={loadCaptcha}
                title="Get new captcha"
              >
                🔄
              </button>
            </div>
            <input
              type="text"
              className="form-input"
              value={captchaText}
              onChange={(e) => setCaptchaText(e.target.value.toUpperCase())}
              placeholder="Enter the code above"
              required
              maxLength={6}
              style={{ textTransform: 'uppercase', letterSpacing: '3px', textAlign: 'center' }}
            />
            <small style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
              Click image to refresh if hard to read
            </small>
          </div>

          <button 
            type="submit" 
            className="btn btn-primary" 
            style={{ width: '100%', marginTop: '10px' }}
            disabled={loading}
          >
            {loading ? 'Creating Account...' : '🎁 Create Account'}
          </button>
        </form>

        <p style={{ textAlign: 'center', marginTop: '25px', color: 'var(--text-secondary)' }}>
          Already have an account?{' '}
          <Link to="/login" className="auth-link">Sign in</Link>
        </p>
      </div>
    </div>
  );
};

export default Signup;
